/**
 * @file Fixed-step simulation loop with render interpolation.
 *
 * Simulation runs at a fixed 60Hz so movement, AI and collision are frame-rate
 * independent and deterministic; rendering runs at display rate and receives an
 * `alpha` in [0,1) for interpolating between the last two fixed states.
 *
 * Driven by `renderer.setAnimationLoop` (NOT requestAnimationFrame) because the
 * WebGPU renderer needs its own async-aware loop.
 *
 * The RENDER rate can be capped (`frameCapHz`, driven automatically by the
 * Engine's perf governor on weak GPUs). Capping paces `onRender` ONLY — the
 * fixed `onFixed` step still runs at 60Hz, just two steps per drawn frame — so
 * simulation rate, determinism and the golden hero checksum are untouched.
 */

/** Longest real frame we are willing to simulate; longer gaps (tab was hidden,
 *  level was building) are clamped instead of replayed. */
const MAX_FRAME_DT = 0.25;

/** Slack on the render-cap period, seconds. See the note at its use site. */
const CAP_EPS = 0.001;

/**
 * @param {Object} opts
 * @param {import('three/webgpu').WebGPURenderer} opts.renderer Drives the loop.
 * @param {number} [opts.fixedStep=1/60] Simulation step in seconds.
 * @param {number} [opts.maxSubSteps=5] Fixed steps allowed per rendered frame.
 * @param {number} [opts.frameCapHz=0] Cap the RENDER rate to this many frames
 *   per second; 0 (the default) renders every tick. Simulation is unaffected.
 * @param {(dt: number) => void} [opts.onFixed] `dt` is always `fixedStep`.
 * @param {(dt: number, alpha: number) => void} [opts.onRender] `dt` is the real
 *   (scaled) delta SINCE THE LAST ACTUAL RENDER — under a cap that spans
 *   several ticks, so dt-driven animation keeps real-time speed; `alpha` is the
 *   leftover accumulator / fixedStep.
 * @returns {import('../types.js').Loop}
 */
export function createLoop({
  renderer,
  fixedStep = 1 / 60,
  maxSubSteps = 5,
  frameCapHz = 0,
  onFixed,
  onRender,
} = {}) {
  if (!renderer) throw new Error('createLoop: `renderer` is required (it drives setAnimationLoop)');

  let running = false;
  let timeScale = 1;
  let acc = 0;
  let last = 0;
  let elapsed = 0;

  /** Render cap in Hz; 0 = uncapped (render every tick, the default path). */
  let frameCap = normalizeHz(frameCapHz);
  /** Phase accumulator for the render cap, in REAL (unscaled) seconds. */
  let renderAcc = 0;
  /** Scaled dt banked since the last `onRender`, so a paced frame reports the
   *  whole span it covers rather than one tick's worth. */
  let renderDt = 0;

  const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

  /** @param {number} [timeMs] Supplied by setAnimationLoop. */
  function tick(timeMs) {
    const t = (typeof timeMs === 'number' ? timeMs : now()) / 1000;

    // First frame after start(): assume one nominal step rather than the gap
    // since page load.
    let raw = last === 0 ? fixedStep : t - last;
    last = t;
    if (!(raw > 0)) raw = 0;
    raw = Math.min(raw, MAX_FRAME_DT);
    const dt = raw * timeScale;

    elapsed += dt;
    acc += dt;

    let steps = 0;
    while (acc >= fixedStep && steps < maxSubSteps) {
      acc -= fixedStep;
      steps++;
      onFixed?.(fixedStep);
    }
    // Spiral-of-death guard: if we hit the cap the machine cannot keep up, so
    // drop the debt instead of accumulating it forever.
    if (steps === maxSubSteps && acc > fixedStep) acc = 0;

    const alpha = acc / fixedStep;

    if (frameCap <= 0) {
      onRender?.(dt, alpha);
      return;
    }

    // Paced render. The accumulator is a PHASE, not a deadline test: we add the
    // real frame time and SUBTRACT one period when it fills, keeping the
    // remainder. `now - lastRender >= period` instead throws the remainder away,
    // and on a 60Hz display a 30Hz cap then needs three vsyncs whenever the
    // frame lands a hair early — 20fps, not 30. Subtracting self-corrects into a
    // clean alternating 30.
    const period = 1 / frameCap;
    renderAcc += raw;
    renderDt += dt;
    // CAP_EPS: two 60Hz vsyncs land a few microseconds either side of a 30Hz
    // period, so an exact `>=` coin-flips between 2 and 3 ticks per frame. The
    // tolerance always fires on the second tick; the FULL period is still
    // subtracted, so `renderAcc` simply dips slightly negative and the long-run
    // rate stays exactly `frameCap`.
    if (renderAcc < period - CAP_EPS) return;

    renderAcc -= period;
    // Still over a period after subtracting means the machine dropped frames.
    // Drop the debt rather than burning renders trying to repay it.
    if (renderAcc > period) renderAcc = 0;

    const spanDt = renderDt;
    renderDt = 0;
    onRender?.(spanDt, alpha);
  }

  return {
    start() {
      if (running) return;
      running = true;
      last = 0;
      acc = 0;
      renderAcc = 0;
      renderDt = 0;
      renderer.setAnimationLoop(tick);
    },
    stop() {
      if (!running) return;
      running = false;
      renderer.setAnimationLoop(null);
    },
    /** @param {number} scale 0 pauses simulation, 1 is real time. */
    setTimeScale(scale) {
      timeScale = Math.max(0, scale || 0);
    },
    /**
     * Caps the RENDER rate. Simulation keeps its fixed 60Hz either way.
     * @param {number|null|false} [hz] Frames per second; 0/null/false/negative
     *   (or anything non-finite) means uncapped.
     */
    setFrameCap(hz) {
      const next = normalizeHz(hz);
      if (next === frameCap) return;
      frameCap = next;
      // Restart the phase from here: the cap normally engages from inside
      // `onRender`, so "now" is exactly when the last frame was drawn.
      renderAcc = 0;
      renderDt = 0;
    },
    /** @returns {number} Current render cap in Hz; 0 when uncapped. */
    frameCap: () => frameCap,
    isRunning: () => running,
    elapsed: () => elapsed,
  };
}

/**
 * @param {any} hz
 * @returns {number} A positive finite Hz, or 0 meaning "uncapped".
 */
function normalizeHz(hz) {
  return typeof hz === 'number' && Number.isFinite(hz) && hz > 0 ? hz : 0;
}
